import OrderItems from "./OrderItems";
import { getMealCount } from "../../utils/orderItemCounts";
import { useState, useEffect } from "react";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Phone,
  Mail,
  Package,
  Coffee,
  Tag,
  Plus,
  User,
  Filter,
  Search,
  RefreshCw,
  GlassWater,
  IceCreamBowl,
  Truck,
  Pencil,
} from "lucide-react";
import Layout from "../../components/layout/Layout";
import orderApi from "../../api/orders";
import Swal from "sweetalert2";
import OrderAssignDialog from "./OrderAssignDialog";
import BulkAssignDialog from "./BulkAssignDialog";
import ChangeAssigneeDialog from "./ChangeAssigneeDialog";
import deliveryAgentsApi from "../../api/delivery-agents";
import OrderLocationsMap from "./OrderLocationsMap";
import SwapMealDialog from "./SwapMealDialog";
import SwapDrinkDialog from "./SwapDrinkDialog";
import AdhocOrderModal from "./AdhocOrderModal";
import useAuth from "../../hooks/useAuth";
import DaySwapSetDialog from "./DaySwapSetDialog";

export const isMeaningfulDiet = (val: any): boolean =>
  !!val &&
  typeof val === "string" &&
  !["everything", "no preference", "none", "n/a", "-", ""].includes(val.trim().toLowerCase());

export const resolveOrderDietaryPreference = (order: any): string => {
  const orderPref = order?.dietaryPreferences || order?.dietaryPreference;
  if (orderPref && typeof orderPref === "string" && orderPref.trim()) {
    const trimmed = orderPref.trim();
    if (trimmed.toLowerCase() === "no preference") return "everything";
    return trimmed;
  }
  const subPref = order?.subscriptionId?.dietaryPreferences || order?.subscriptionId?.dietaryPreference;
  if (subPref && typeof subPref === "string" && subPref.trim()) {
    const trimmed = subPref.trim();
    if (trimmed.toLowerCase() === "no preference") return "everything";
    return trimmed;
  }
  return "everything";
};

const OrderDayExplorer = () => {
  const { admin } = useAuth();
  const isAdminUser = admin?.role === "admin" || admin?.role === "super-admin";
  // State management
  const [date, setDate] = useState(new Date());
  const [daySwapSetOpen, setDaySwapSetOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [isAdhocModalOpen, setIsAdhocModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState<{
    totalOrders: number;
    totalMeals: number;
    totalDrinks: number;
    mealsWithAddons: { count: number; percentage: number };
    addOnCounts: Record<string, number>;
    revenue: number;
    ordersWithCoconutWater: number;
    mealsToday: Record<string, any>;
    drinksToday: Record<string, { count: number; type: string }>;
  }>({
    totalOrders: 0,
    totalMeals: 0,
    totalDrinks: 0,
    mealsWithAddons: { count: 0, percentage: 0 },
    addOnCounts: {},
    revenue: 0,
    ordersWithCoconutWater: 0,
    mealsToday: {},
    drinksToday: {},
  });
  const [mealStats, setMealStats] = useState({
    overall: {
      total: 0,
      assigned: 0,
      completed: 0,
      pending: 0,
    },
    slotWise: [],
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(Number.MAX_SAFE_INTEGER);
  // Meal plan distribution data
  const [mealPlanData, setMealPlanData] = useState([]);
  const [postCodeFilter, setPostCodeFilter] = useState("");
  const [timeSlotFilter, setTimeSlotFilter] = useState("");
  const [uniquePostCodes, setUniquePostCodes] = useState([]);
  const [timeSlotsData, setTimeSlotsData] = useState([]);
  // New state for assignment dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deliveryAgentFilter, setDeliveryAgentFilter] = useState("");
  const [deliveryAgents, setDeliveryAgents] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 0,
    pages: 0,
  });
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [changeAssigneeDialogOpen, setChangeAssigneeDialogOpen] =
    useState(false);
  const [orderToChangeAssignee, setOrderToChangeAssignee] = useState(null);
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [orderToSwap, setOrderToSwap] = useState(null);
  const [swapDrinkDialogOpen, setSwapDrinkDialogOpen] = useState(false);
  const [orderToSwapDrink, setOrderToSwapDrink] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Fetch orders data
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        // Create date range for API call (full day)
        const startDate = new Date(date);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(date);
        endDate.setHours(23, 59, 59, 999);

        const { orders, pagination } = await orderApi.getOrders({
          status: statusFilter,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          page: page + 1,
          limit: rowsPerPage,
        });

        setOrders(orders);
        setPagination(pagination);

        // map of each mealId.name and its detailed batch breakdown
        const mealCounts: Record<string, {
          total: number;
          classicTotal: number;
          classicNormal: number;
          classicLacFree: number;
          lightTotal: number;
          lightNormal: number;
          lightLacFree: number;
        }> = {};

        orders.forEach((order: any) => {
          const diet = resolveOrderDietaryPreference(order).toLowerCase();
          const isLacFree = diet.includes("lactose-free") || diet.includes("dairy-free");

          if (order.meals && order.meals.length > 0) {
            order.meals.forEach((m: any) => {
              const mealName = m.mealId?.name || order.mealId?.name || "Unknown Meal";
              if (!mealCounts[mealName]) {
                mealCounts[mealName] = {
                  total: 0,
                  classicTotal: 0,
                  classicNormal: 0,
                  classicLacFree: 0,
                  lightTotal: 0,
                  lightNormal: 0,
                  lightLacFree: 0,
                };
              }
              mealCounts[mealName].total += 1;
              const pSize = (m.portionSize || order.portionSize || order.subscriptionId?.portionSize || "classic").toLowerCase();
              if (pSize === "light") {
                mealCounts[mealName].lightTotal += 1;
                if (isLacFree) mealCounts[mealName].lightLacFree += 1;
                else mealCounts[mealName].lightNormal += 1;
              } else {
                mealCounts[mealName].classicTotal += 1;
                if (isLacFree) mealCounts[mealName].classicLacFree += 1;
                else mealCounts[mealName].classicNormal += 1;
              }
            });
          } else if (order.mealId && order.mealId.name) {
            const mealName = order.mealId?.name || "Unknown Meal";
            const count = order?.peopleCount || 1;
            if (!mealCounts[mealName]) {
              mealCounts[mealName] = {
                total: 0,
                classicTotal: 0,
                classicNormal: 0,
                classicLacFree: 0,
                lightTotal: 0,
                lightNormal: 0,
                lightLacFree: 0,
              };
            }
            mealCounts[mealName].total += count;
            const pSize = (order.portionSize || order.subscriptionId?.portionSize || "classic").toLowerCase();
            if (pSize === "light") {
              mealCounts[mealName].lightTotal += count;
              if (isLacFree) mealCounts[mealName].lightLacFree += count;
              else mealCounts[mealName].lightNormal += count;
            } else {
              mealCounts[mealName].classicTotal += count;
              if (isLacFree) mealCounts[mealName].classicLacFree += count;
              else mealCounts[mealName].classicNormal += count;
            }
          }
        });

        // Calculate stats
        const totalRevenue = orders.reduce(
          (sum, order) => sum + order.amount,
          0,
        );

        // number of meals (peopleCount) with plan title as "Power Morning Plan"
        const ordersWithCoconutWater = orders
          .filter(
            (order) =>
              order.subscriptionId?.planId?.title === "Power Morning Plan",
          )
          .reduce((sum, order) => sum + (order?.peopleCount || 0), 0);

        // Aggregate add-ons weighted by peopleCount
        const addOnCounts = orders.reduce(
          (acc, order) => {
            const count = order?.peopleCount || 0;
            const addOns = order?.addOns || [];
            addOns.forEach((name) => {
              acc[name] = (acc[name] || 0) + count;
            });
            return acc;
          },
          {} as Record<string, number>,
        );

        // Aggregate drinks
        const drinkCounts: Record<string, { count: number; type: string }> = {};
        let totalDrinksCount = 0;
        orders.forEach((order: any) => {
          if (order.drinks && order.drinks.length > 0) {
            order.drinks.forEach((d: any) => {
              const drinkObj = d.drinkId || order.drinkId;
              if (drinkObj) {
                const drinkName = drinkObj.name || "Drink";
                const type = drinkObj.type || d.drinkType || "coffee";
                if (!drinkCounts[drinkName]) {
                  drinkCounts[drinkName] = { count: 0, type };
                }
                drinkCounts[drinkName].count += 1;
                totalDrinksCount += 1;
              }
            });
          } else if (order.drinkId) {
            const drinkName = order.drinkId.name || "Drink";
            const type = order.drinkId.type || "coffee";
            const count = order?.peopleCount || 1;
            if (!drinkCounts[drinkName]) {
              drinkCounts[drinkName] = { count: 0, type };
            }
            drinkCounts[drinkName].count += count;
            totalDrinksCount += count;
          }
        });

        setStats({
          totalOrders: pagination.total,
          totalMeals: orders.reduce((sum, order) => sum + getMealCount(order), 0),
          totalDrinks: totalDrinksCount,
          ordersWithCoconutWater,
          mealsWithAddons: {
            count: orders.reduce(
              (sum, order) => sum + (order.addOns.length > 0 ? 1 : 0),
              0,
            ),
            percentage: Math.round(
              (orders.reduce(
                (sum, order) => sum + (order.addOns.length > 0 ? 1 : 0),
                0,
              ) /
                orders.length) *
                100,
            ),
          },
          addOnCounts,
          mealsToday: mealCounts,
          drinksToday: drinkCounts,
          revenue: totalRevenue,
        });

        // group all orders by subscriptionId.plan.title
        const groupedOrders = orders.reduce((acc, order) => {
          const planTitle = order.subscriptionId?.planId?.title;
          if (planTitle) {
            acc[planTitle] = (acc[planTitle] || 0) + 1;
          }
          return acc;
        }, {});

        const colors = ["#4F46E5", "#10B981", "#F59E0B"];

        const mealPlanData = Object.entries(groupedOrders).map(
          ([key, value], index) => ({
            name: key,
            value: value as number,
            percentage: Math.round(((value as number) / orders.length) * 100),
            color: colors[index],
          }),
        );

        setMealPlanData(mealPlanData);

        const postcodes = Array.from(
          new Set(
            orders
              .map((order) => order.deliveryDetails?.postcode)
              .filter(Boolean),
          ),
        );
        setUniquePostCodes(postcodes);

        // Calculate time slots distribution
        const timeSlots = orders.reduce((acc, order) => {
          const timeSlot = order.deliveryTime || "Not specified";
          acc[timeSlot] = (acc[timeSlot] || 0) + 1;
          return acc;
        }, {});

        const timeSlotsChartData = Object.entries(timeSlots).map(
          ([name, value]) => ({
            name,
            value,
          }),
        );

        setTimeSlotsData(timeSlotsChartData);

        // Calculate meal statistics from orders data
        const totalMeals = orders.reduce(
          (sum, order) => sum + getMealCount(order),
          0,
        );
        const assignedMeals = orders
          .filter((order) => order.deliveryAgentId)
          .reduce((sum, order) => sum + getMealCount(order), 0);
        const completedMeals = orders
          .filter((order) => order.status === "completed")
          .reduce((sum, order) => sum + getMealCount(order), 0);
        const pendingMeals = orders
          .filter((order) => order.status === "pending")
          .reduce((sum, order) => sum + getMealCount(order), 0);

        // Calculate slot-wise statistics
        const slotStats = {};
        orders.forEach((order) => {
          const timeSlot = order.deliveryTime || "Not specified";
          if (!slotStats[timeSlot]) {
            slotStats[timeSlot] = {
              total: 0,
              assigned: 0,
              completed: 0,
              pending: 0,
              meals: {},
              drinks: {},
              addOns: {},
            };
          }

          slotStats[timeSlot].total += getMealCount(order);
          if (order.deliveryAgentId) {
            slotStats[timeSlot].assigned += getMealCount(order);
          }
          if (order.status === "completed") {
            slotStats[timeSlot].completed += getMealCount(order);
          }
          if (order.status === "pending") {
            slotStats[timeSlot].pending += getMealCount(order);
          }

          // Count meals (per-person, respecting meals[] swaps)
          if (order.meals && order.meals.length > 0) {
            order.meals.forEach((m) => {
              const mealName =
                m.mealId?.name || order.mealId?.name || "Unknown Meal";
              slotStats[timeSlot].meals[mealName] =
                (slotStats[timeSlot].meals[mealName] || 0) + 1;
            });
          } else if (order.mealId) {
            const mealName = order.mealId?.name || "Unknown Meal";
            slotStats[timeSlot].meals[mealName] =
              (slotStats[timeSlot].meals[mealName] || 0) + order.peopleCount;
          }

          // Count drinks
          if (order.drinks && order.drinks.length > 0) {
            order.drinks.forEach((d: any) => {
              const drinkObj = d.drinkId || order.drinkId;
              if (drinkObj) {
                const drinkName = drinkObj.name || "Drink";
                const type = drinkObj.type || d.drinkType || "coffee";
                if (!slotStats[timeSlot].drinks[drinkName]) {
                  slotStats[timeSlot].drinks[drinkName] = { count: 0, type };
                }
                slotStats[timeSlot].drinks[drinkName].count += 1;
              }
            });
          } else if (order.drinkId) {
            const drinkName = order.drinkId.name || "Drink";
            const type = order.drinkId.type || "coffee";
            const count = order?.peopleCount || 1;
            if (!slotStats[timeSlot].drinks[drinkName]) {
              slotStats[timeSlot].drinks[drinkName] = { count: 0, type };
            }
            slotStats[timeSlot].drinks[drinkName].count += count;
          }

          // Count add-ons
          if (order.addOns && order.addOns.length > 0) {
            order.addOns.forEach((addOn) => {
              slotStats[timeSlot].addOns[addOn] =
                (slotStats[timeSlot].addOns[addOn] || 0) + order.peopleCount;
            });
          }
        });

        // Convert slot stats to array format
        const slotStatsArray = Object.entries(slotStats).map(
          ([slot, stats]: [string, any]) => ({
            slot,
            total: stats.total,
            assigned: stats.assigned,
            completed: stats.completed,
            pending: stats.pending,
            meals: stats.meals,
            drinks: stats.drinks,
            addOns: stats.addOns,
          }),
        );

        setMealStats({
          overall: {
            total: totalMeals,
            assigned: assignedMeals,
            completed: completedMeals,
            pending: pendingMeals,
          },
          slotWise: slotStatsArray,
        });
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchDeliveryAgents = async () => {
      const { deliveryAgents } = await deliveryAgentsApi.getDeliveryAgents();
      setDeliveryAgents(deliveryAgents);
    };

    fetchOrders();
    fetchDeliveryAgents();
  }, [date, statusFilter, page, rowsPerPage, refreshKey]);

  // Filter orders by search term
  const filteredOrders = orders.filter((order) => {
    if (
      !searchTerm &&
      !postCodeFilter &&
      !timeSlotFilter &&
      !deliveryAgentFilter
    )
      return true;

    const term = searchTerm.toLowerCase();
    const matchesSearch =
      order.userId?.name?.toLowerCase().includes(term) ||
      order.userId?.email?.toLowerCase().includes(term) ||
      order.userId?.phoneNumber?.toLowerCase().includes(term) ||
      order.deliveryDetails?.postcode?.toLowerCase().includes(term);

    const matchesPostcode = postCodeFilter
      ? order.deliveryDetails?.postcode === postCodeFilter
      : true;

    const matchesTimeSlot = timeSlotFilter
      ? order.deliveryTime === timeSlotFilter
      : true;

    const matchesDeliveryAgent = deliveryAgentFilter
      ? order.deliveryAgentId?._id === deliveryAgentFilter
      : true;

    return (
      matchesSearch &&
      matchesPostcode &&
      matchesTimeSlot &&
      matchesDeliveryAgent
    );
  });

  // Orders by pincode data
  const pincodeData = orders
    .reduce((acc, order) => {
      const postcode = order.deliveryDetails?.postcode;
      if (postcode) {
        const existingEntry = acc.find((item) => item.pincode === postcode);
        if (existingEntry) {
          existingEntry.orders += 1;
          existingEntry.orderIds.push(order._id);
        } else {
          acc.push({
            pincode: postcode,
            orders: 1,
            orderIds: [order._id],
          });
        }
      }
      return acc;
    }, [])
    .sort((a, b) => b.orders - a.orders);

  // Format date as string
  const formatDate = (dateObj) => {
    return dateObj.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Navigation functions
  const goToPreviousDay = () => {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() - 1);
    setDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + 1);
    //Difference between newDate & currentDate
    const diff = newDate.getTime() - new Date().getTime();
    const diffDays = Math.ceil(diff / (1000 * 60 * 60 * 24));
    if (!isAdminUser && diffDays <= 3) {
      setDate(newDate);
    } else if (isAdminUser) {
      setDate(newDate);
    }
  };

  const goToToday = () => {
    setDate(new Date());
  };

  const getOrderLocationQuery = (order: any) => {
    if (!order) return null;

    const coords =
      order.deliveryDetails?.coordinates ||
      order.subscriptionId?.deliveryDetails?.coordinates;

    const rawLat =
      coords?.latitude ??
      coords?.lat ??
      order.deliveryLocation?.lat ??
      order.deliveryLocation?.latitude;

    const rawLng =
      coords?.longitude ??
      coords?.lng ??
      order.deliveryLocation?.lng ??
      order.deliveryLocation?.longitude;

    const lat = typeof rawLat === "string" ? parseFloat(rawLat) : rawLat;
    const lng = typeof rawLng === "string" ? parseFloat(rawLng) : rawLng;

    if (
      typeof lat === "number" &&
      typeof lng === "number" &&
      !isNaN(lat) &&
      !isNaN(lng) &&
      (lat !== 0 || lng !== 0)
    ) {
      return { type: "coords" as const, query: `${lat},${lng}` };
    }

    // Fallback: Text address + pincode
    const dd = order.deliveryDetails || order.subscriptionId?.deliveryDetails;
    if (dd) {
      const parts = [
        dd.flatNumber,
        dd.address,
        dd.city,
        dd.state,
        dd.postcode,
      ]
        .filter((p: any) => typeof p === "string" && p.trim().length > 0)
        .map((p: string) => p.trim());

      if (parts.length > 0) {
        return { type: "address" as const, query: encodeURIComponent(parts.join(", ")) };
      }
    }

    return null;
  };

  const openGoogleMapsLink = (order: any) => {
    const loc = getOrderLocationQuery(order);
    if (loc) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${loc.query}`,
        "_blank",
      );
    } else {
      Swal.fire({
        icon: "warning",
        title: "No Location Found",
        text: "This order has neither GPS coordinates nor a delivery address saved.",
        confirmButtonColor: "#f97316",
      });
    }
  };

  // Get status color based on order status
  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-emerald-100 text-emerald-800";
      case "Processing":
        return "bg-amber-100 text-amber-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleAssign = (order) => {
    setSelectedOrder(order);
    setAssignDialogOpen(true);
  };

  const downloadReport = async (reportType) => {
    try {
      const reportData = {
        orders: filteredOrders,
        stats: stats,
        date: formatDate(date),
        reportType,
      };

      // Show loading state
      Swal.fire({
        title: "Generating PDF Report",
        text: "Please wait while we generate your report...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const pdfBlob = await orderApi.generateOrderReportPdf(reportData);

      // Create download link
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportType}-report-${formatDate(date).replace(/[^a-zA-Z0-9]/g, "-")}.pdf`;
      if (reportType === "delivery") {
        const deliveryAgent = deliveryAgents.find(
          (agent) => agent._id === deliveryAgentFilter,
        );
        if (deliveryAgent) {
          a.download = `${deliveryAgent?.name}-${formatDate(date).replace(/[^a-zA-Z0-9]/g, "-")}.pdf`;
        }
      }
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      Swal.fire({
        title: "Success",
        text: "PDF report downloaded successfully",
        icon: "success",
      });
    } catch (error) {
      console.error("Error downloading report:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to download PDF report",
        icon: "error",
      });
    }
  };

  const openSwapDialog = (order, mealIndex = 0) => {
    setOrderToSwap({ ...order, mealIndex });
    setSwapDialogOpen(true);
  };

  const swapOrderMeal = async (order, newMeal) => {
    try {
      await orderApi.swapMeal(order._id, newMeal._id, order.mealIndex);
      Swal.fire({
        title: "Success",
        text: "Meal swapped successfully",
        icon: "success",
      });
      // Trigger a refetch to keep all stats/charts in sync
      setRefreshKey((k) => k + 1);
    } catch (error) {
      console.error("Error swapping meal:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to swap meal",
        icon: "error",
      });
      throw error;
    }
  };

  const openDrinkSwapDialog = (order: any, drinkIndex = 0) => {
    setOrderToSwapDrink({ ...order, drinkIndex });
    setSwapDrinkDialogOpen(true);
  };

  const swapOrderDrink = async (order: any, newDrinkId: string | null) => {
    try {
      await orderApi.swapDrink(order._id, newDrinkId, order.drinkIndex);
      Swal.fire({
        title: "Success",
        text: newDrinkId ? "Drink updated successfully" : "Drink removed successfully",
        icon: "success",
      });
      // Trigger a refetch to keep all stats/charts in sync
      setRefreshKey((k) => k + 1);
    } catch (error: any) {
      console.error("Error updating drink:", error);
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || "Failed to update drink",
        icon: "error",
      });
      throw error;
    }
  };

  const handleEditDietaryPreference = async (order: any) => {
    const rawPref = resolveOrderDietaryPreference(order);
    const normalizedPref = ["lactose-free", "dairy-free", "gluten-free"].includes(rawPref.toLowerCase())
      ? rawPref.toLowerCase()
      : "everything";

    const { value: newPref } = await Swal.fire({
      title: "Update Dietary Preference",
      html: `<div class="text-sm text-gray-600 mb-2">Order for <b>${order.deliveryDetails?.name || order.userId?.name || "Customer"}</b></div>`,
      input: "select",
      inputOptions: {
        "everything": "Everything",
        "lactose-free": "Lactose-Free",
        "dairy-free": "Dairy-Free",
        "gluten-free": "Gluten-Free",
      },
      inputValue: normalizedPref,
      showCancelButton: true,
      confirmButtonText: "Save",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        confirmButton:
          "px-5 py-2 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 shadow-sm mx-1.5 cursor-pointer outline-none transition-colors",
        cancelButton:
          "px-5 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg border border-gray-300 hover:bg-gray-200 shadow-sm mx-1.5 cursor-pointer outline-none transition-colors",
        popup: "rounded-2xl p-6",
        input: "!border !border-gray-300 !rounded-lg !py-2 !px-3 !text-sm !text-gray-800 !mt-2",
      },
    });

    if (newPref !== undefined) {
      try {
        await orderApi.updateOrderDietaryPreference(order._id, newPref);
        const displayLabel = newPref === "everything" ? "Everything" : newPref;
        Swal.fire({
          title: "Updated!",
          text: `Dietary preference updated to ${displayLabel}`,
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        setRefreshKey((k) => k + 1);
      } catch (error: any) {
        console.error("Error updating dietary preference:", error);
        Swal.fire({
          title: "Error",
          text: error?.response?.data?.message || "Failed to update dietary preference",
          icon: "error",
        });
      }
    }
  };

  const assignDeliveryAgent = async (order, agent, sequenceDetails) => {
    const ordersToUpdate = [order._id];
    const deliveryAgentId = agent._id;

    if (sequenceDetails) {
      await orderApi.sequenceOrder(order._id, {
        deliveryAgentId,
        ...sequenceDetails,
      });
    } else {
      await orderApi.assignDeliveryAgent(ordersToUpdate, deliveryAgentId);
    }
    setAssignDialogOpen(false);
    Swal.fire({
      title: "Success",
      text: "Delivery agent assigned successfully",
      icon: "success",
    });

    //update the order status in the orders array
    const updatedOrders = orders.map((item) =>
      item._id === order._id
        ? {
            ...item,
            deliveryAgentId: agent,
            assigned: true,
            ...(sequenceDetails || {}),
          }
        : item,
    );
    setOrders(updatedOrders);
  };

  const bulkAssignDeliveryAgent = async (agent, sequenceDetails) => {
    try {
      const deliveryAgentId = agent._id;

      if (Array.isArray(sequenceDetails)) {
        const sequences = sequenceDetails.map((item) => ({
          orderId: item._id,
          deliveryAgentId,
          batchNumber: item.batchNumber,
          batchDate: item.batchDate,
          sequenceInBatch: item.sequenceInBatch,
        }));
        await orderApi.bulkSequenceOrders(sequences);

        const itemMap = new Map(sequenceDetails.map((s) => [s._id, s]));
        const updatedOrders = orders.map((item) => {
          if (itemMap.has(item._id)) {
            const seqInfo = itemMap.get(item._id);
            return {
              ...item,
              deliveryAgentId: agent,
              assigned: true,
              batchNumber: seqInfo.batchNumber,
              batchDate: seqInfo.batchDate,
              sequenceInBatch: seqInfo.sequenceInBatch,
            };
          }
          return item;
        });
        setOrders(updatedOrders);
      } else if (sequenceDetails) {
        const orderIds = selectedOrders.map((order) => order._id);
        const sequences = orderIds.map((orderId, index) => ({
          orderId,
          deliveryAgentId,
          batchNumber: sequenceDetails.batchNumber,
          batchDate: sequenceDetails.batchDate,
          sequenceInBatch: index + 1,
        }));
        await orderApi.bulkSequenceOrders(sequences);

        const updatedOrders = orders.map((item) => {
          const isSelected = selectedOrders.some((selected) => selected._id === item._id);
          if (isSelected) {
            const idx = orderIds.indexOf(item._id);
            return {
              ...item,
              deliveryAgentId: agent,
              assigned: true,
              ...(sequenceDetails || {}),
              sequenceInBatch: idx !== -1 ? idx + 1 : 1,
            };
          }
          return item;
        });
        setOrders(updatedOrders);
      } else {
        const orderIds = selectedOrders.map((order) => order._id);
        await orderApi.assignDeliveryAgent(orderIds, deliveryAgentId);

        const updatedOrders = orders.map((item) =>
          selectedOrders.some((selected) => selected._id === item._id)
            ? { ...item, deliveryAgentId: agent, assigned: true }
            : item,
        );
        setOrders(updatedOrders);
      }

      setBulkAssignDialogOpen(false);
      setSelectedOrders([]);

      Swal.fire({
        title: "Success",
        text: `${selectedOrders.length} orders assigned successfully`,
        icon: "success",
      });
    } catch (error) {
      console.error("Error bulk assigning orders:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to assign orders",
        icon: "error",
      });
    }
  };

  const changeOrderAssignee = async (order, agent, sequenceDetails) => {
    try {
      const ordersToUpdate = [order._id];
      const deliveryAgentId = agent._id;

      if (sequenceDetails) {
        await orderApi.sequenceOrder(order._id, {
          deliveryAgentId,
          ...sequenceDetails,
        });
      } else {
        await orderApi.assignDeliveryAgent(ordersToUpdate, deliveryAgentId);
      }
      setChangeAssigneeDialogOpen(false);
      setOrderToChangeAssignee(null);

      Swal.fire({
        title: "Success",
        text: "Delivery agent changed successfully",
        icon: "success",
      });

      // Update the order in the orders array
      const updatedOrders = orders.map((item) =>
        item._id === order._id
          ? {
              ...item,
              deliveryAgentId: agent,
              ...(sequenceDetails || {}),
            }
          : item,
      );
      setOrders(updatedOrders);
    } catch (error) {
      console.error("Error changing assignee:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to change delivery agent",
        icon: "error",
      });
    }
  };

  const handleOrderSelection = (order, isSelected) => {
    if (isSelected) {
      setSelectedOrders([...selectedOrders, order]);
    } else {
      setSelectedOrders(
        selectedOrders.filter((item) => item._id !== order._id),
      );
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedOrders(filteredOrders);
    } else {
      setSelectedOrders([]);
    }
  };

  const handleStatusChange = async (orderId, status) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: `Order will be marked ${status}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, change it!",
        customClass: {
          confirmButton:
            "bg-green-500 text-white font-bold py-2 px-4 rounded mr-2 hover:bg-green-600",
          cancelButton:
            "bg-red-500 text-white font-bold py-2 px-4 rounded hover:bg-red-600",
        },
        buttonsStyling: false,
      });

      if (result.isConfirmed) {
        await orderApi.updateOrderStatus(orderId, status);
        Swal.fire({
          title: "Updated!",
          text: `The order status has been updated to ${status}.`,
          icon: "success",
          confirmButtonColor: "#10B981",
          customClass: {
            confirmButton:
              "bg-green-500 text-white font-bold py-2 px-4 rounded mr-2 hover:bg-green-600",
          },
          buttonsStyling: false,
        });
        //update the order status in the orders array
        const updatedOrders = orders.map((order) =>
          order._id === orderId ? { ...order, status } : order,
        );
        setOrders(updatedOrders);
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      Swal.fire({
        title: "Error",
        text: "There was a problem updating the order status.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#10B981",
        customClass: {
          confirmButton:
            "bg-red-500 text-white font-bold py-2 px-4 rounded mr-2 hover:bg-red-600",
        },
        buttonsStyling: false,
      });
    }
  };

  // Handle status filter
  const handleStatusFilterChange = (status) => {
    setStatusFilter(status === statusFilter ? "" : status);
    setPage(0); // Reset page when filter changes
  };

  // Handle search
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 p-2 sm:p-4 md:p-6 w-full max-w-full overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Order Day Explorer
          </h1>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
              <button
                onClick={goToPreviousDay}
                className="p-2 hover:bg-gray-50 border-r border-gray-200"
                aria-label="Previous day"
              >
                <ChevronLeft className="h-5 w-5 text-gray-500" />
              </button>

              <div className="px-4 py-2 flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span className="font-medium text-gray-700">
                  {formatDate(date)}
                </span>
              </div>

              <button
                onClick={goToNextDay}
                className="p-2 hover:bg-gray-50 border-l border-gray-200"
                aria-label="Next day"
              >
                <ChevronRight className="h-5 w-5 text-gray-500" />
              </button>

              <button
                onClick={goToToday}
                className="px-4 py-2 bg-orange-50 text-orange-600 font-semibold border-l border-gray-200 hover:bg-orange-100 transition-colors text-[0.95rem]"
              >
                Today
              </button>
            </div>

            <button
              onClick={() => {
                setStatusFilter("");
                setSearchTerm("");
                setDate(new Date());
              }}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
              aria-label="Reset filters"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
            <button
              onClick={() => setDaySwapSetOpen(true)}
              className="px-4 py-2 bg-white border border-gray-200 text-gray-700 hover:bg-orange-50 rounded-lg flex items-center gap-2 shadow-sm"
            >
              <Package className="h-4 w-4" />
              View swap set
            </button>
            {isAdminUser && (
              <button
                onClick={() => setIsAdhocModalOpen(true)}
                className="px-4 py-2 bg-orange-600 text-white hover:bg-orange-700 rounded-lg flex items-center shadow-sm ml-2 text-[0.95rem]"
                aria-label="Add Ad-Hoc Order"
              >
                <Plus className="h-5 w-5" />
                <span className="hidden sm:inline ml-2 font-medium">
                  Ad-Hoc Order
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Top Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Orders */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-500">
                Total Orders
              </h3>
              <span className="p-2 bg-orange-50 rounded-full">
                <Package className="h-5 w-5 text-orange-500" />
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900 mb-1">
              {pagination?.total}
            </p>
            <p className="text-sm text-gray-500">For {formatDate(date)}</p>
          </div>

          {/* Total Meals */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-500">Total Meals</h3>
              <span className="p-2 bg-emerald-50 rounded-full">
                <Coffee className="h-5 w-5 text-emerald-500" />
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900 mb-1">
              {stats.totalMeals}
            </p>
            <p className="text-sm text-gray-500">Across all orders</p>
          </div>

          {/* Total Drinks */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-500">Total Drinks</h3>
              <span className="p-2 bg-amber-50 rounded-full">
                <GlassWater className="h-5 w-5 text-amber-600" />
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900 mb-1">
              {stats.totalDrinks || 0}
            </p>
            <p className="text-sm text-gray-500">Across all orders</p>
          </div>

          {/* Meals with AddOns */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-500">With Add-ons</h3>
              <span className="p-2 bg-amber-50 rounded-full">
                <Plus className="h-5 w-5 text-amber-500" />
              </span>
            </div>
            <p className="text-sm text-gray-900 mb-1 whitespace-pre-wrap">
              {Object.keys(stats.addOnCounts || {}).length === 0
                ? "No add-ons"
                : Object.entries(stats.addOnCounts as Record<string, number>)
                    .map(([name, count]) => `${name}: ${count}`)
                    .join("\n")}
            </p>
          </div>
        </div>

        {/* Dedicated Full-Width Meal Prep Breakdown */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3.5 sm:p-5 md:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <span className="p-2.5 bg-orange-100 text-orange-600 rounded-xl shadow-sm">
                <IceCreamBowl className="h-6 w-6" />
              </span>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Today's Meal Prep Breakdown</h3>
                <p className="text-xs text-gray-500 font-medium">Portion (Classic vs Light) and dietary counts across all scheduled meals</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold px-3 py-1 rounded-lg bg-gray-100 text-gray-800 border border-gray-200">
                {Object.keys(stats.mealsToday).length} Distinct Meals
              </span>
              <span className="text-xs font-bold px-3 py-1 rounded-lg bg-orange-500 text-white shadow-sm">
                {stats.totalMeals} Total Meals
              </span>
            </div>
          </div>

          {Object.entries(stats.mealsToday).length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center font-medium">No meals scheduled for this day</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Object.entries(stats.mealsToday).map(([meal, detail]: [string, any]) => {
                const isObj = typeof detail === "object" && detail !== null;
                const total = isObj ? detail.total : detail;
                return (
                  <div
                    key={meal}
                    className="p-4 rounded-2xl bg-white border-2 border-gray-200 shadow-sm hover:shadow-md hover:border-orange-300 transition-all flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex justify-between items-start gap-2 mb-3">
                        <h4 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2">
                          {meal}
                        </h4>
                        <span className="flex-shrink-0 text-xs font-black px-2.5 py-1 rounded-md bg-orange-500 text-white shadow-sm">
                          {total}
                        </span>
                      </div>

                      {isObj && (
                        <div
                          className={`grid ${
                            (detail.lightTotal || 0) > 0 && (detail.classicTotal || 0) > 0
                              ? "grid-cols-1 sm:grid-cols-2"
                              : "grid-cols-1"
                          } gap-2 mt-2`}
                        >
                          {/* Classic */}
                          {((detail.classicTotal || 0) > 0 || (detail.lightTotal || 0) === 0) && (
                            <div
                              className="p-2.5 rounded-xl border-2 bg-purple-50/90 border-purple-300 text-purple-950"
                            >
                              <div className="flex justify-between items-center pb-1.5 border-b border-purple-200/80 mb-2">
                                <span className="text-[11px] font-black uppercase tracking-wider text-purple-900">
                                  Classic
                                </span>
                                <span className="text-base font-black text-purple-950">
                                  {detail.classicTotal}
                                </span>
                              </div>
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between items-center">
                                  <span className="text-purple-800 font-semibold">Normal:</span>
                                  <span className="font-black text-sm text-gray-900">
                                    {detail.classicNormal}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-purple-800 font-semibold">Lac-Free:</span>
                                  <span
                                    className={`font-black text-sm px-1.5 py-0.5 rounded ${
                                      detail.classicLacFree > 0
                                        ? "text-amber-900 bg-amber-200 border border-amber-300"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    {detail.classicLacFree}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Light */}
                          {(detail.lightTotal || 0) > 0 && (
                            <div
                              className="p-2.5 rounded-xl border-2 bg-sky-50/90 border-sky-300 text-sky-950"
                            >
                              <div className="flex justify-between items-center pb-1.5 border-b border-sky-200/80 mb-2">
                                <span className="text-[11px] font-black uppercase tracking-wider text-sky-900">
                                  Light
                                </span>
                                <span className="text-base font-black text-sky-950">
                                  {detail.lightTotal}
                                </span>
                              </div>
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between items-center">
                                  <span className="text-sky-800 font-semibold">Normal:</span>
                                  <span className="font-black text-sm text-gray-900">
                                    {detail.lightNormal}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-sky-800 font-semibold">Lac-Free:</span>
                                  <span
                                    className={`font-black text-sm px-1.5 py-0.5 rounded ${
                                      detail.lightLacFree > 0
                                        ? "text-amber-900 bg-amber-200 border border-amber-300"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    {detail.lightLacFree}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Charts Row */}
        {/* <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 mb-6"> */}
        {/* Meal Plan Distribution */}
        {/* <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Meal Plan Distribution
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={mealPlanData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    paddingAngle={2}
                    dataKey="value"
                    labelLine={false}
                    label={({ name, percentage }) => `${name} (${percentage}%)`}
                  >
                    {mealPlanData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name, props) => [`${value} meals`, name]}
                    contentStyle={{
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center mt-4 space-x-6">
              {mealPlanData.map((entry, index) => (
                <div key={index} className="flex items-center">
                  <span
                    className="inline-block w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: entry?.color }}
                  ></span>
                  <span className="text-sm text-gray-600">
                    {entry?.name}:{" "}
                    <span className="font-medium">{entry?.value}</span>
                  </span>
                </div>
              ))}
            </div>
          </div> */}
        {/* </div> */}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Orders by Pincode */}
          {/* <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Orders by Pincode
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pincodeData}>
                  <XAxis dataKey="pincode" />
                  <YAxis />
                  <Tooltip
                    formatter={(value, name) => [
                      value,
                      "Orders",
                    ]}
                    contentStyle={{
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="orders"
                    name="Orders"
                    fill="#4F46E5"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2 max-h-36 overflow-y-auto">
              {pincodeData.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center py-2 border-b border-gray-100"
                >
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-700">
                      Pincode {item.pincode}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-indigo-600">
                    {item.orders} orders
                  </span>
                </div>
              ))}
            </div>
          </div> */}

          {/* Orders by Time Slot */}
          {/* <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Orders by Time Slot
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeSlotsData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip
                    formatter={(value, name) => [value, name === "Orders"]}
                  />
                  <Legend />
                  <Bar dataKey="value" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div> */}
        </div>

        {/* Slot-wise Meal Statistics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Meal Statistics by Time Slot
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time Slot
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Meals
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completed
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pending
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {mealStats.slotWise.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      No meal statistics available for the selected date
                    </td>
                  </tr>
                ) : (
                  mealStats.slotWise.map((slot, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-gray-900">
                          {slot.slot}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900 font-medium">
                          {slot.total}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-orange-600 font-medium">
                          {slot.assigned}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-emerald-600 font-medium">
                          {slot.completed}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-amber-600 font-medium">
                          {slot.pending}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-emerald-500 h-2 rounded-full"
                              style={{
                                width: `${slot.total > 0 ? (slot.completed / slot.total) * 100 : 0}%`,
                              }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-500">
                            {slot.total > 0
                              ? Math.round((slot.completed / slot.total) * 100)
                              : 0}
                            %
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Dedicated Full-Width Drink Prep Breakdown */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3.5 sm:p-5 md:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <span className="p-2.5 bg-amber-100 text-amber-700 rounded-xl shadow-sm">
                <GlassWater className="h-6 w-6" />
              </span>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Today's Drink Prep Breakdown</h3>
                <p className="text-xs text-gray-500 font-medium">Fresh cold brew coffees and nutrient blends across all scheduled orders</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold px-3 py-1 rounded-lg bg-gray-100 text-gray-800 border border-gray-200">
                {Object.keys(stats.drinksToday || {}).length} Distinct Drinks
              </span>
              <span className="text-xs font-bold px-3 py-1 rounded-lg bg-amber-600 text-white shadow-sm">
                {stats.totalDrinks || 0} Total Drinks
              </span>
            </div>
          </div>

          {Object.entries(stats.drinksToday || {}).length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center font-medium">No drinks scheduled for this day</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Object.entries(stats.drinksToday || {}).map(([drinkName, detail]: [string, any]) => {
                const count = typeof detail === "object" ? detail.count : detail;
                const isCoffee = typeof detail === "object" && detail.type === "coffee";
                return (
                  <div
                    key={drinkName}
                    className="p-4 rounded-2xl bg-white border-2 border-gray-200 shadow-sm hover:shadow-md hover:border-amber-300 transition-all flex flex-col justify-between"
                  >
                    <div className="flex justify-between items-start gap-2 mb-2">
                      <h4 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2">
                        {drinkName}
                      </h4>
                      <span className="flex-shrink-0 text-xs font-black px-2.5 py-1 rounded-md bg-amber-600 text-white shadow-sm">
                        {count}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-2">
                      <span
                        className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                          isCoffee
                            ? "bg-amber-100 text-amber-800 border border-amber-300"
                            : "bg-emerald-100 text-emerald-800 border border-emerald-300"
                        }`}
                      >
                        {isCoffee ? "Coffee" : "Blend"}
                      </span>
                      <span className="text-xs text-gray-500 font-medium">
                        {count} {count === 1 ? "bottle / cup" : "bottles / cups"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Meal & Drink Breakdown by Time Slot */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Meal & Drink Breakdown by Time Slot
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mealStats.slotWise.length === 0 ? (
              <p className="text-gray-500 col-span-full text-center py-4">
                No statistics available.
              </p>
            ) : (
              mealStats.slotWise.map((slot: any, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium text-orange-600">{slot.slot}</h4>
                    <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                      Total: {slot.total}
                    </span>
                  </div>

                  {/* Meals in slot */}
                  <div className="space-y-2">
                    {Object.keys(slot.meals || {}).length === 0 ? (
                      <p className="text-xs text-gray-400 italic">
                        No meals recorded
                      </p>
                    ) : (
                      Object.entries(slot.meals || {})
                        .sort(([, a], [, b]) => (b as number) - (a as number))
                        .map(([meal, count]) => (
                          <div
                            key={meal}
                            className="flex justify-between items-center text-sm"
                          >
                            <span
                              className="text-gray-700 truncate mr-2"
                              title={meal}
                            >
                              {meal}
                            </span>
                            <span className="font-medium text-gray-900">
                              {count as number}
                            </span>
                          </div>
                        ))
                    )}
                  </div>

                  {/* Drinks in slot */}
                  {Object.keys(slot.drinks || {}).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <h5 className="text-xs font-semibold text-amber-700 mb-2 uppercase tracking-wide flex items-center gap-1">
                        <GlassWater className="h-3.5 w-3.5" />
                        <span>Drinks</span>
                      </h5>
                      <div className="space-y-1">
                        {Object.entries(slot.drinks || {})
                          .sort(([, a]: any, [, b]: any) => (b.count || b) - (a.count || a))
                          .map(([drink, detail]: any) => {
                            const count = typeof detail === "object" ? detail.count : detail;
                            const isCoffee = typeof detail === "object" && detail.type === "coffee";
                            return (
                              <div
                                key={drink}
                                className="flex justify-between items-center text-sm"
                              >
                                <div className="flex items-center gap-1.5 truncate mr-2">
                                  <span
                                    className="text-gray-700 truncate"
                                    title={drink}
                                  >
                                    {drink}
                                  </span>
                                  <span className={`px-1 py-0.2 text-[8px] font-bold rounded ${
                                    isCoffee
                                      ? "bg-amber-100 text-amber-800"
                                      : "bg-emerald-100 text-emerald-800"
                                  }`}>
                                    {isCoffee ? "Coffee" : "Blend"}
                                  </span>
                                </div>
                                <span className="font-semibold text-amber-900">
                                  {count}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Add-ons breakdown */}
                  {Object.keys(slot.addOns || {}).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <h5 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                        Add-ons
                      </h5>
                      <div className="space-y-1">
                        {Object.entries(slot.addOns || {})
                          .sort(([, a], [, b]) => (b as number) - (a as number))
                          .map(([addOn, count]) => (
                            <div
                              key={addOn}
                              className="flex justify-between items-center text-sm"
                            >
                              <span
                                className="text-gray-600 truncate mr-2"
                                title={addOn}
                              >
                                {addOn}
                              </span>
                              <span className="font-medium text-gray-800">
                                {count as number}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Order Locations Map */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 mb-20">
          <div className="p-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Order Locations
            </h3>

            <OrderLocationsMap orders={orders} />
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-5 border-b border-gray-200">
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Orders Overview</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Showing {filteredOrders.length} of {stats.totalOrders} total orders
                  {selectedOrders.length > 0 && (
                    <span className="ml-1.5 text-red-600 font-semibold">({selectedOrders.length} selected)</span>
                  )}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {selectedOrders.length > 0 && (
                  <button
                    onClick={() => setBulkAssignDialogOpen(true)}
                    className="text-xs font-bold text-white bg-red-600 hover:bg-red-700 active:scale-95 rounded-lg px-3.5 py-2 flex items-center shadow-xs transition-all cursor-pointer"
                    type="button"
                    aria-label="Bulk Assign Orders"
                  >
                    <User className="h-4 w-4 mr-1.5" />
                    Assign {selectedOrders.length} Orders
                  </button>
                )}

                <button
                  onClick={() => downloadReport("admin")}
                  className="text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100/80 border border-rose-200/80 active:scale-95 rounded-lg px-3 py-2 flex items-center transition-all cursor-pointer"
                  type="button"
                  aria-label="Admin Report"
                >
                  <Package className="h-3.5 w-3.5 mr-1.5 text-rose-600" />
                  Admin Report
                </button>

                <button
                  onClick={() => downloadReport("kitchen")}
                  className="text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100/80 border border-emerald-200/80 active:scale-95 rounded-lg px-3 py-2 flex items-center transition-all cursor-pointer"
                  type="button"
                  aria-label="Kitchen Report"
                >
                  <Coffee className="h-3.5 w-3.5 mr-1.5 text-emerald-600" />
                  Kitchen Report
                </button>

                <button
                  onClick={() => downloadReport("kitchen-batches")}
                  className="text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100/80 border border-indigo-200/80 active:scale-95 rounded-lg px-3 py-2 flex items-center transition-all cursor-pointer"
                  type="button"
                  aria-label="Rider Batches"
                >
                  <Truck className="h-3.5 w-3.5 mr-1.5 text-indigo-600" />
                  Rider Batches
                </button>

                <button
                  onClick={() => downloadReport("delivery")}
                  className="text-xs font-semibold text-amber-800 bg-amber-50 hover:bg-amber-100/80 border border-amber-200/80 active:scale-95 rounded-lg px-3 py-2 flex items-center transition-all cursor-pointer"
                  type="button"
                  aria-label="Delivery Report"
                >
                  <MapPin className="h-3.5 w-3.5 mr-1.5 text-amber-600" />
                  Delivery Sheet
                </button>

                <button
                  onClick={() => downloadReport("labels")}
                  className="text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100/80 border border-purple-200/80 active:scale-95 rounded-lg px-3 py-2 flex items-center transition-all cursor-pointer"
                  type="button"
                  aria-label="Kitchen Labels"
                >
                  <Tag className="h-3.5 w-3.5 mr-1.5 text-purple-600" />
                  Print Labels
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              {/* Search */}
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                />
              </div>

              {/* Delivery Agent filter */}
              <select
                value={deliveryAgentFilter}
                onChange={(e) => setDeliveryAgentFilter(e.target.value)}
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              >
                <option value="">All Delivery Agents</option>
                {deliveryAgents.map(
                  (agent) =>
                    agent?.isActive === true && (
                      <option key={agent?._id} value={agent?._id}>
                        {agent?.name}
                      </option>
                    ),
                )}
              </select>

              {/* Postcode filter */}
              <select
                value={postCodeFilter}
                onChange={(e) => setPostCodeFilter(e.target.value)}
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              >
                <option value="">All Postcodes</option>
                {uniquePostCodes.map((postcode) => (
                  <option key={postcode} value={postcode}>
                    {postcode}
                  </option>
                ))}
              </select>

              {/* Time slot filter */}
              <select
                value={timeSlotFilter}
                onChange={(e) => setTimeSlotFilter(e.target.value)}
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              >
                <option value="">All Time Slots</option>
                {timeSlotsData.map((slot) => (
                  <option key={slot?.name} value={slot?.name}>
                    {slot?.name}
                  </option>
                ))}
              </select>

              {/* Status filters */}
              <div className="flex space-x-2">
                <button
                  onClick={() => handleStatusFilterChange("completed")}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border ${
                    statusFilter === "Completed"
                      ? "bg-emerald-300 text-emerald-800 border-emerald-200"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <span className="flex items-center">
                    <Filter className="h-4 w-4 mr-1" />
                    Completed
                  </span>
                </button>

                <button
                  onClick={() => handleStatusFilterChange("pending")}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border ${
                    statusFilter === "Pending"
                      ? "bg-gray-300 text-gray-800 border-gray-300"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <span className="flex items-center">
                    <Filter className="h-4 w-4 mr-1" />
                    Pending
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={
                        selectedOrders.length === filteredOrders.length &&
                        filteredOrders.length > 0
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    S.No.
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order Items
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cutlery
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-">
                    Dietary Preferences
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Additional Notes
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delivery Preferences
                  </th>
                  {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delivery Date
                  </th> */}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delivery Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pinned Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delivery Agent
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Change Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y-2 divide-slate-300 [&>tr>td]:align-top [&>tr>td]:py-3">
                {loading ? (
                  <tr>
                    <td colSpan={13} className="px-6 py-12 text-center">
                      <div className="flex justify-center items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                        <span className="ml-3 text-gray-500">
                          Loading orders...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : filteredOrders.length === 0 ? (
                  <tr>
                    <td
                      colSpan={13}
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      No orders found for the selected criteria
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order: any, rowIndex: number) => {
                    const isAdhoc =
                      order?.isAdhoc ||
                      (!order?.subscriptionId && order?.type !== "instant");
                    const isSelected = selectedOrders.some(selected => selected._id === order._id);
                    return (
                      <tr
                        key={order._id || order.id}
                        className={`transition-colors hover:bg-amber-50 ${
                          isSelected
                            ? "bg-orange-50"
                            : isAdhoc
                            ? "bg-rose-50"
                            : order.isSwapped
                            ? "bg-orange-50"
                            : rowIndex % 2 === 0 ? "bg-white" : "bg-slate-50"
                        }`}
                      >
                        <td className={`border-l-4 px-2 py-2 ${isSelected ? "border-l-orange-500" : isAdhoc ? "border-l-rose-400" : order.isSwapped ? "border-l-orange-400" : "border-l-slate-300"}`}>
                          <input
                            type="checkbox"
                            checked={selectedOrders.some(
                              (selected) => selected._id === order._id,
                            )}
                            onChange={(e) =>
                              handleOrderSelection(order, e.target.checked)
                            }
                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                          />
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">
                          {page * rowsPerPage + filteredOrders.indexOf(order) + 1}
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center">
                              <User className="h-5 w-5 text-orange-500" />
                            </div>
                            <div className="ml-4">
                              <div className="font-medium text-gray-900 flex items-center flex-wrap gap-2">
                                <span>
                                  {order?.deliveryDetails?.name &&
                                  order.deliveryDetails?.name !==
                                    order.userId.name
                                    ? order.deliveryDetails.name +
                                      " " +
                                      "(" +
                                      order?.userId?.name +
                                      ")"
                                    : order?.userId?.name}
                                </span>
                                {isAdhoc && (
                                  <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-800 border border-red-200 uppercase tracking-wider">
                                    Ad-Hoc
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 flex items-center mt-1">
                                <Phone className="h-3 w-3 mr-1" />
                                {order?.deliveryDetails?.phoneNumber &&
                                order.deliveryDetails.phoneNumber !==
                                  order?.userId?.phoneNumber ? (
                                  order.deliveryDetails.phoneNumber +
                                  " " +
                                  "(" +
                                  order?.userId?.phoneNumber +
                                  ")"
                                ) : order?.userId?.phoneNumber ? (
                                  <a
                                    href={`tel:${order.userId.phoneNumber}`}
                                    className="text-orange-600 hover:text-orange-800 underline"
                                  >
                                    {order.userId.phoneNumber}
                                  </a>
                                ) : (
                                  "N/A"
                                )}
                              </div>
                              <div className="text-xs text-gray-500 flex items-center mt-1">
                                <Mail className="h-3 w-3 mr-1" />
                                {order?.userId?.email || "N/A"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <div className="text-xs text-gray-900">
                            {order?.deliveryDetails?.flatNumber}{" "}
                            {order?.deliveryDetails?.address}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 flex items-center">
                            <MapPin className="h-3 w-3 mr-1" />
                            Pincode: {order?.deliveryDetails?.postcode}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <OrderItems order={order}
                            onSwapMeal={index => openSwapDialog(order, index)}
                            onSwapDrink={index => openDrinkSwapDialog(order, index)} />
                        </td>
                        <td className="px-2 py-2 text-xs">
                          {order?.requireCutlery ? "Yes" : "No"}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          <div className="flex items-center gap-1.5 group">
                            {(() => {
                              const pref = resolveOrderDietaryPreference(order);
                              const lower = pref.trim().toLowerCase();
                              const isLac = lower.includes("lactose-free") || lower.includes("dairy-free");
                              const isGlutenFree = lower.includes("gluten-free");
                              if (isLac) {
                                return (
                                  <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-amber-50 text-amber-800 border border-amber-200 inline-block capitalize">
                                    {pref}
                                  </span>
                                );
                              }
                              if (isGlutenFree) {
                                return (
                                  <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-teal-50 text-teal-800 border border-teal-200 inline-block capitalize">
                                    {pref}
                                  </span>
                                );
                              }
                              if (lower === "everything") {
                                return <span className="text-gray-700">Everything</span>;
                              }
                              return (
                                <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-purple-50 text-purple-800 border border-purple-200 inline-block capitalize">
                                  {pref}
                                </span>
                              );
                            })()}
                            {order.status === "pending" && (
                              <button
                                onClick={() => handleEditDietaryPreference(order)}
                                title="Edit dietary preference"
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-orange-50 text-gray-400 hover:text-orange-600 rounded cursor-pointer"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-xs">
                          {order?.subscriptionId?.deliveryDetails
                            ?.additionalNote || "N/A"}
                        </td>
                        <td className="px-2 py-2 text-xs">
                          {order?.subscriptionId?.deliveryDetails
                            ?.deliveryInstructions?.length > 0
                            ? order?.subscriptionId?.deliveryDetails?.deliveryInstructions?.join(
                                ", ",
                              )
                            : "N/A"}
                        </td> <td className="px-2 py-2 text-xs">
                        {order?.deliveryTime}
                      </td>
                        <td className="px-2 py-2">
                          <div className="flex space-x-1">
                            {(() => {
                              const hasLoc = !!getOrderLocationQuery(order);
                              return (
                                <button
                                  onClick={() => openGoogleMapsLink(order)}
                                  title={
                                    hasLoc
                                      ? "Open in Google Maps"
                                      : "No coordinates or address available"
                                  }
                                  className={`px-2 py-1 text-xs font-medium rounded border flex items-center gap-1 transition-colors ${
                                    hasLoc
                                      ? "border-gray-300 bg-white text-gray-700 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700 cursor-pointer shadow-xs"
                                      : "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                                  }`}
                                >
                                  <MapPin className={`h-3 w-3 ${hasLoc ? "text-orange-600" : "text-gray-400"}`} />
                                  Map
                                </button>
                              );
                            })()}
                          </div>
                        </td>
                      <td className="px-2 py-2">
                        {order.status === "pending" &&
                          (!order.assigned ? (
                            <button
                              onClick={() => handleAssign(order)}
                              className="px-2 py-1 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                            >
                              Assign
                            </button>
                          ) : (
                            <div className="space-y-1">
                              <span className="py-1 text-xs font-medium block">
                                Assigned to {order?.deliveryAgentId?.name} (
                                {order?.deliveryAgentId?.phoneNumber ? (
                                  <a
                                    href={`tel:${order.deliveryAgentId.phoneNumber}`}
                                    className="text-orange-600 hover:text-orange-800 underline"
                                  >
                                    {order.deliveryAgentId.phoneNumber}
                                  </a>
                                ) : (
                                  "N/A"
                                )}
                                )
                              </span>
                              {order.batchNumber && (
                                <span className="text-[10px] bg-orange-100 text-orange-800 border border-orange-200 px-1.5 py-0.5 rounded font-bold inline-block mt-0.5 mr-1">
                                  Batch {order.batchNumber} • Stop{" "}
                                  {order.sequenceInBatch}
                                </span>
                              )}
                              {order.isFlagged && (
                                <span className="text-[10px] bg-red-100 text-red-800 border border-red-200 px-1.5 py-0.5 rounded font-bold inline-block mt-0.5">
                                  ⚠️ Issue: {order.flagReason}
                                </span>
                              )}
                              <button
                                onClick={() => {
                                  setOrderToChangeAssignee(order);
                                  setChangeAssigneeDialogOpen(true);
                                }}
                                className="px-2 py-1 text-xs font-medium rounded border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 block mt-1"
                              >
                                Change
                              </button>
                            </div>
                          ))}
                        {order.status === "completed" && (
                          <div className="space-y-1">
                            <span className="py-1 text-xs font-medium block">
                              Delivered by {order?.deliveryAgentId?.name} (
                              {order?.deliveryAgentId?.phoneNumber ? (
                                <a
                                  href={`tel:${order.deliveryAgentId.phoneNumber}`}
                                  className="text-orange-600 hover:text-orange-800 underline"
                                >
                                  {order.deliveryAgentId.phoneNumber}
                                </a>
                              ) : (
                                "N/A"
                              )}
                              )
                            </span>
                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                              <button
                                onClick={() => {
                                  setOrderToChangeAssignee(order);
                                  setChangeAssigneeDialogOpen(true);
                                }}
                                className="px-2 py-0.5 text-[11px] font-medium rounded border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100"
                              >
                                Change
                              </button>
                              {order.deliveryMode === "hand_delivery" && (
                                <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded font-bold">
                                  Hand Delivered
                                </span>
                              )}
                              {order.deliveryMode === "doorstep_photo" && (
                                <span className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded font-bold">
                                  Doorstep Drop
                                </span>
                              )}
                              {order.deliveryPhotoUrl && (
                                <a
                                  href={order.deliveryPhotoUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="px-2 py-0.5 text-[11px] font-medium rounded border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 inline-flex items-center"
                                >
                                View Photo
                                </a>
                              )}
                              {order.deliveryLocation?.lat && order.deliveryLocation?.lng && (
                                <a
                                  href={`https://www.google.com/maps/search/?api=1&query=${order.deliveryLocation.lat},${order.deliveryLocation.lng}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="px-2 py-0.5 text-[11px] font-medium rounded border border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100 inline-flex items-center"
                                  title={`Latitude: ${order.deliveryLocation.lat}, Longitude: ${order.deliveryLocation.lng}`}
                                >
                                  📍 View Location
                                </a>
                              )}
                              {order.callAttempts && order.callAttempts.length > 0 && (
                                <span
                                  className="text-[10px] bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded font-bold cursor-help inline-flex items-center"
                                  title={order.callAttempts
                                    .map(
                                      (c: any, i: number) =>
                                        `Call #${i + 1}: ${new Date(c.timestamp).toLocaleTimeString()}`
                                    )
                                    .join("\n")}
                                >
                                  📞 Called ({order.callAttempts.length})
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        {order.status === "pending" && (
                          <button
                            id={order?.id}
                            onClick={() =>
                              handleStatusChange(order?._id, "completed")
                            }
                            style={{
                              cursor:
                                !order.assigned && !(order.isAdhoc || (!order.subscriptionId && order.type !== "instant"))
                                  ? "not-allowed"
                                  : "pointer",
                              opacity:
                                !order.assigned && !(order.isAdhoc || (!order.subscriptionId && order.type !== "instant")) ? 0.5 : 1,
                              pointerEvents:
                                !order.assigned && !(order.isAdhoc || (!order.subscriptionId && order.type !== "instant"))
                                  ? "none"
                                  : "auto",
                            }}
                            disabled={!order.assigned && !(order.isAdhoc || (!order.subscriptionId && order.type !== "instant"))}
                            className="px-2 py-1 text-xs font-medium rounded bg-emerald-500 text-white hover:bg-emerald-600"
                          >
                            Complete
                          </button>
                        )}
                        {order.status === "completed" && (
                          <span className="px-2 py-1 text-xs font-medium rounded bg-emerald-700 text-white">
                            Completed
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(Math.max(0, page - 1))}
                disabled={page === 0}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  page === 0
                    ? "bg-gray-100 text-gray-400"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Previous
              </button>
              <button
                disabled={
                  page === Math.ceil(pagination.total / rowsPerPage) - 1
                }
                onClick={() => handlePageChange(page + 1)}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md bg-white text-gray-700 hover:bg-gray-50"
              >
                Next
              </button>
            </div>

            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">{page * rowsPerPage + 1}</span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min((page + 1) * rowsPerPage, pagination.total)}
                  </span>{" "}
                  of <span className="font-medium">{pagination.total}</span>{" "}
                  results
                </p>
              </div>
              {/* <div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => handlePageChange(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                      page === 0
                        ? "text-gray-300"
                        : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  {[...Array(Math.min(3, 4))].map((_, i) => (
                    <button
                      key={i}
                      disabled={
                        page === Math.ceil(pagination.total / rowsPerPage) - 1
                      }
                      onClick={() => handlePageChange(i)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === i
                          ? "z-10 bg-indigo-50 border-indigo-500 text-indigo-600"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    disabled={
                      page === Math.ceil(pagination.total / rowsPerPage) - 1
                    }
                    onClick={() => handlePageChange(page + 1)}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div> */}
            </div>
          </div>
        </div>

        {/* Footer section */}
        <div className="mt-8 pt-4 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-500 mb-4 md:mb-0">
              Order Day Explorer • Last updated:{" "}
              {new Date().toLocaleTimeString()}
            </p>
          </div>
        </div>
      </div>

      <OrderAssignDialog
        isOpen={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        onAssign={assignDeliveryAgent}
        order={selectedOrder}
      />

      <BulkAssignDialog
        isOpen={bulkAssignDialogOpen}
        onClose={() => setBulkAssignDialogOpen(false)}
        onAssign={bulkAssignDeliveryAgent}
        selectedOrders={selectedOrders}
      />

      <ChangeAssigneeDialog
        isOpen={changeAssigneeDialogOpen}
        onClose={() => {
          setChangeAssigneeDialogOpen(false);
          setOrderToChangeAssignee(null);
        }}
        onAssign={changeOrderAssignee}
        order={orderToChangeAssignee}
      />

      <SwapMealDialog
        isOpen={swapDialogOpen}
        onClose={() => {
          setSwapDialogOpen(false);
          setOrderToSwap(null);
        }}
        order={orderToSwap}
        onSwap={swapOrderMeal}
      />

      <SwapDrinkDialog
        isOpen={swapDrinkDialogOpen}
        onClose={() => {
          setSwapDrinkDialogOpen(false);
          setOrderToSwapDrink(null);
        }}
        order={orderToSwapDrink}
        onSwap={swapOrderDrink}
      />

      <AdhocOrderModal
        open={isAdhocModalOpen}
        onClose={() => setIsAdhocModalOpen(false)}
        onSuccess={() => setRefreshKey((k) => k + 1)}
        selectedDate={date}
      />
      <DaySwapSetDialog open={daySwapSetOpen} onClose={() => setDaySwapSetOpen(false)} date={date} />
    </Layout>
  );
};

export default OrderDayExplorer;
